import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import {
  selectLoading as selectTransactionCategoriesLoading,
  selectTransactionCategory,
  selectTransactionCategoryOptions,
} from '_store/transactionCategories/selector';
import {
  addTransactionCategory,
  getTransactionCategories,
  refreshCategory as refreshNewTransactionCategory,
} from '_store/transactionCategories/slice';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import CheckboxField from 'components/atomic/fields/CheckboxField';
import CreatableSelectField from 'components/atomic/fields/CreatableSelectField';
import DateField from 'components/atomic/fields/DateField';
import MaskField from 'components/atomic/fields/MaskField';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const EditTransactionForm = ({ loading, error, submitDisabled, defaultValues, onCancel, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const transactionCategoryOptions = useSelector(selectTransactionCategoryOptions);
  const transactionCategoriesLoading = useSelector(selectTransactionCategoriesLoading);
  const newTransactionCategory = useSelector(selectTransactionCategory);

  const values = useWatch({ control });

  const handleCreateTransactionCategoryOption = (category) => {
    dispatch(addTransactionCategory({ customerId, category }));
    dispatch(refreshNewTransactionCategory(category));
  };

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    const newTransactionCategoryValue = transactionCategoryOptions?.find(
      ({ title }) => title === newTransactionCategory
    )?.value;

    if (newTransactionCategoryValue) {
      setValue(FIELD_KEYS.accountName, newTransactionCategoryValue);
      dispatch(refreshNewTransactionCategory(null));
    }
  }, [transactionCategoryOptions, newTransactionCategory]);

  useEffect(() => {
    dispatch(getTransactionCategories({ customerId }));
  }, []);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={6}>
          <SelectField
            {...getFieldProps(FIELD_KEYS.transactionType)}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={6}>
          <DateField
            {...getFieldProps(FIELD_KEYS.date, {
              validationOptions: {
                values,
              },
            })}
            icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
            dateFormat="MM/dd/yyyy"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12}>
          <CreatableSelectField
            {...getFieldProps(FIELD_KEYS.accountName)}
            loading={transactionCategoriesLoading}
            options={transactionCategoryOptions}
            onCreateOption={handleCreateTransactionCategoryOption}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12}>
          <BaseField {...getFieldProps(FIELD_KEYS.contactName)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <SelectField {...getFieldProps(FIELD_KEYS.reference)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <MaskField {...getFieldProps(FIELD_KEYS.total)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12}>
          <CheckboxField
            {...getFieldProps(FIELD_KEYS.reconcile)}
            control={control}
            disabled
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={6}>
          <Button outlined onClick={onCancel}>
            Cancel
          </Button>
        </Col>
        <Col xs={6}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.indigo.primary}>
            Submit
          </Button>
        </Col>
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

EditTransactionForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditTransactionForm;
